/**
 * ai-agent.effects.ts — NgRx effects wiring HTTP + WebSocket services to actions.
 */
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap, exhaustMap } from 'rxjs/operators';

import * as AiAgentActions from '../actions/ai-agent.actions';
import { AiAgentHttpService } from '../services/ai-agent-http.service';
import { AiAgentSocketService } from '../services/ai-agent-socket.service';
import { AiBackendError } from '../interfaces/ai-backend-error';

@Injectable()
export class AiAgentEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly httpService: AiAgentHttpService,
        private readonly socketService: AiAgentSocketService,
    ) {}

    // ── Submit Job ─────────────────────────────────────────────────────

    submitJob$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AiAgentActions.submitJob),
            switchMap(({ request }) =>
                this.httpService.submitJob$(request).pipe(
                    map((response) => AiAgentActions.submitJobSuccess({ sessionId: response.sessionId })),
                    catchError((err) =>
                        of(AiAgentActions.submitJobFailed({
                            error: AiBackendError.from(err, 'Failed to submit AI job'),
                        })),
                    ),
                ),
            ),
        ),
    );

    // ── Auto-poll session after job is submitted ───────────────────────

    autoPollOnSubmit$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AiAgentActions.submitJobSuccess),
            map(({ sessionId }) => AiAgentActions.pollSession({ sessionId })),
        ),
    );

    // ── Poll Session ──────────────────────────────────────────────────

    pollSession$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AiAgentActions.pollSession),
            switchMap(({ sessionId }) =>
                this.httpService.getSession$(sessionId).pipe(
                    map((session) => AiAgentActions.pollSessionSuccess({ session })),
                    catchError((err) =>
                        of(AiAgentActions.pollSessionFailed({
                            error: AiBackendError.from(err, 'Failed to poll session'),
                        })),
                    ),
                ),
            ),
        ),
    );

    // ── Re-poll session when job completes (to get full output) ───────

    pollOnCompleted$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AiAgentActions.jobCompletedReceived),
            switchMap(({ event }) =>
                of(AiAgentActions.pollSession({ sessionId: event.sessionId })),
            ),
        ),
    );

    // ── WebSocket: connect ────────────────────────────────────────────

    connectSocket$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(AiAgentActions.connectSocket),
                tap(({ token }) => this.socketService.connect(token)),
            ),
        { dispatch: false },
    );

    // ── WebSocket: disconnect ─────────────────────────────────────────

    disconnectSocket$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(AiAgentActions.disconnectSocket),
                tap(() => this.socketService.disconnect()),
            ),
        { dispatch: false },
    );

    // ── WebSocket: mirror connection state into the store ─────────────

    syncSocketConnected$ = createEffect(() =>
        this.socketService.isConnected$.pipe(
            map((connected) => AiAgentActions.socketConnectedChanged({ connected })),
        ),
    );

    // ── WebSocket: forward progress events to store ───────────────────

    forwardProgress$ = createEffect(() =>
        this.socketService.allProgress$.pipe(
            map((event) => AiAgentActions.jobProgressReceived({ event })),
        ),
    );

    // ── WebSocket: forward completed events to store ──────────────────

    forwardCompleted$ = createEffect(() =>
        this.socketService.allCompleted$.pipe(
            map((event) => AiAgentActions.jobCompletedReceived({ event })),
        ),
    );

    // ── Chat ──────────────────────────────────────────────────────────

    sendChat$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AiAgentActions.sendChat),
            exhaustMap(({ sessionId, message, sectionKey }) =>
                this.httpService.sendChat$(sessionId, message, sectionKey).pipe(
                    map((response) => AiAgentActions.sendChatSuccess({
                        sessionId: response.sessionId,
                        reply: response.reply,
                        ...(response.replyKey ? { replyKey: response.replyKey } : {}),
                        ...(response.replyParams ? { replyParams: response.replyParams } : {}),
                        output: response.output,
                    })),
                    catchError((err) =>
                        of(AiAgentActions.sendChatFailed({
                            error: AiBackendError.from(err, 'Failed to send chat message'),
                        })),
                    ),
                ),
            ),
        ),
    );

    // ── Reset: disconnect socket on session reset ─────────────────────

    disconnectOnReset$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(AiAgentActions.resetSession),
                tap(() => this.socketService.disconnect()),
            ),
        { dispatch: false },
    );
}
